import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Card } from '../components/Card';
import { dashboardService, DashboardHomeData } from '../services/dashboard.service';
import { formatCompactCurrency } from '../utils/formatters';

export const DashboardHome: React.FC = () => {
  const [data, setData] = useState<DashboardHomeData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const dashboardData = await dashboardService.getDashboardHome();
        setData(dashboardData);
      } catch (err) {
        setError('Failed to load dashboard data');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Helper function to determine color based on progress toward target
  const getTargetColor = (current: number, target: number): string => {
    if (current >= target) return 'success'; // Green if on or above target
    return 'warning'; // Amber if below target
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
          <p className="mt-4 text-text-secondary">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <p className="text-danger text-lg">{error || 'No data available'}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-background text-text-primary">
      {/* Header */}
      <header className="bg-surface border-b-4 border-primary">
        <div className="max-w-full mx-auto px-12 py-8">
          <div className="text-center">
            <h1 className="text-5xl font-bold text-primary mb-6">
              Sales Dashboard
            </h1>
            <p className="text-xl text-text-secondary mb-2">
              Path to Targets: March 2026 ({formatCompactCurrency(data.config.march2026Target)}) | FY26 ({formatCompactCurrency(data.config.fy26Target)}) | FY27 ({formatCompactCurrency(data.config.fy27Target)})
            </p>
            <p className="text-sm text-text-muted">
              Last Updated: {new Date().toLocaleDateString('en-GB', { year: 'numeric', month: 'long', day: 'numeric' })}
            </p>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-full mx-auto px-12 py-8">
        {/* Key Achievement Box */}
        <div className="bg-surface border-4 border-success rounded-lg p-8 mb-8 text-center">
          <h2 className="text-2xl font-bold text-success mb-4">
            🎯 PATH TO ALL TARGETS
          </h2>
          <div className="grid grid-cols-3 gap-8">
            <div>
              <div className="text-sm text-text-muted uppercase mb-2">March 2026</div>
              <div className={`text-4xl font-bold text-${getTargetColor(data.overview.march2026TotalPipeline, data.config.march2026Target)} my-4`}>
                {formatCompactCurrency(data.overview.march2026TotalPipeline)}
              </div>
              {data.overview.march2026Breakdown && (
                <p className="text-sm text-text-secondary mb-2">{data.overview.march2026Breakdown}</p>
              )}
              <p className="text-sm text-text-secondary">{data.overview.activeMarch2026Deals} Active Deals</p>
            </div>
            <div>
              <div className="text-sm text-text-muted uppercase mb-2">FY 2026 (Apr-Jun)</div>
              <div className={`text-4xl font-bold text-${getTargetColor(data.overview.fy26TotalPipeline, data.config.fy26Target)} my-4`}>
                {formatCompactCurrency(data.overview.fy26TotalPipeline)}
              </div>
              {data.overview.fy26Breakdown && (
                <p className="text-sm text-text-secondary mb-2">{data.overview.fy26Breakdown}</p>
              )}
              <p className="text-sm text-text-secondary">{data.overview.activeFY26Deals} Active Deals</p>
            </div>
            <div>
              <div className="text-sm text-text-muted uppercase mb-2">FY 2027</div>
              <div className={`text-4xl font-bold text-${getTargetColor(data.overview.fy27TotalPipeline, data.config.fy27Target)} my-4`}>
                {formatCompactCurrency(data.overview.fy27TotalPipeline)}
              </div>
              {data.overview.fy27Breakdown && (
                <p className="text-sm text-text-secondary mb-2">{data.overview.fy27Breakdown}</p>
              )}
              <p className="text-sm text-text-secondary">{data.overview.activeFY27Deals} Active Deals</p>
            </div>
          </div>
        </div>

        {/* Quick Overview */}
        <section className="bg-surface border-2 border-border rounded-lg p-8 mb-8">
          <h2 className="text-2xl font-semibold text-primary mb-6 pb-4 border-b-2 border-primary">
            Quick Overview
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
            <div className="bg-background border-2 border-border rounded-lg p-6 text-center">
              <div className="text-sm text-text-muted uppercase mb-2">Current Base</div>
              <div className="text-3xl font-bold text-primary">
                {formatCompactCurrency(data.config.currentEBITDA)}
              </div>
            </div>
            <div className={`bg-background border-2 border-${getTargetColor(data.overview.march2026TotalPipeline, data.config.march2026Target)} rounded-lg p-6 text-center`}>
              <div className="text-sm text-text-muted uppercase mb-2">March 2026 Target</div>
              <div className={`text-3xl font-bold text-${getTargetColor(data.overview.march2026TotalPipeline, data.config.march2026Target)}`}>
                {formatCompactCurrency(data.config.march2026Target)}
              </div>
            </div>
            <div className={`bg-background border-2 border-${getTargetColor(data.overview.fy26TotalPipeline, data.config.fy26Target)} rounded-lg p-6 text-center`}>
              <div className="text-sm text-text-muted uppercase mb-2">FY 2026 Target</div>
              <div className={`text-3xl font-bold text-${getTargetColor(data.overview.fy26TotalPipeline, data.config.fy26Target)}`}>
                {formatCompactCurrency(data.config.fy26Target)}
              </div>
            </div>
            <div className={`bg-background border-2 border-${getTargetColor(data.overview.fy27TotalPipeline, data.config.fy27Target)} rounded-lg p-6 text-center`}>
              <div className="text-sm text-text-muted uppercase mb-2">FY 2027 Target</div>
              <div className={`text-3xl font-bold text-${getTargetColor(data.overview.fy27TotalPipeline, data.config.fy27Target)}`}>
                {formatCompactCurrency(data.config.fy27Target)}
              </div>
            </div>
            <div className="bg-background border-2 border-border rounded-lg p-6 text-center">
              <div className="text-sm text-text-muted uppercase mb-2">Total Active Deals</div>
              <div className="text-3xl font-bold text-primary">
                {data.overview.activeMarch2026Deals + data.overview.activeFY26Deals + data.overview.activeFY27Deals}
              </div>
            </div>
          </div>
        </section>

        {/* Executive Summary Dashboards */}
        <section className="bg-surface border-2 border-border rounded-lg p-8 mb-8">
          <h2 className="text-2xl font-semibold text-primary mb-6 pb-4 border-b-2 border-primary">📊 Executive Summary Dashboards</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Link to="/overview">
              <Card className="hover:border-primary transition-colors cursor-pointer h-full">
                <div className="text-4xl mb-4">📖</div>
                <h3 className="font-semibold text-primary mb-2">Overview & Instructions</h3>
                <p className="text-sm text-text-muted">Complete guide to dashboard objectives, review process, data sources, and how to use the system effectively</p>
              </Card>
            </Link>
            <Link to="/executive-summary">
              <Card className="hover:border-primary transition-colors cursor-pointer h-full">
                <div className="text-4xl mb-4">📈</div>
                <h3 className="font-semibold text-primary mb-2">Executive Summary</h3>
                <p className="text-sm text-text-muted">Strategic path to EBITDA targets with complete build-up analysis and confidence breakdown</p>
              </Card>
            </Link>
            <Link to="/team-summary">
              <Card className="hover:border-primary transition-colors cursor-pointer h-full">
                <div className="text-4xl mb-4">👥</div>
                <h3 className="font-semibold text-primary mb-2">Team Summary</h3>
                <p className="text-sm text-text-muted">Individual performance tracking with on-track indicators and pipeline values</p>
              </Card>
            </Link>
          </div>
        </section>

        {/* Individual Sales Dashboards */}
        <section className="bg-surface border-2 border-border rounded-lg p-8 mb-8">
          <h2 className="text-2xl font-semibold text-primary mb-6 pb-4 border-b-2 border-primary">👤 Individual Sales Dashboards</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {data.teamSummary.map((rep) => (
              <Link key={rep.id} to={`/individual/${rep.id}`}>
                <Card className="hover:border-primary transition-colors cursor-pointer h-full">
                  <div className="text-4xl mb-4">
                    {rep.name.includes('Alex') ? '🎯' :
                     rep.name.includes('Marcus') ? '🎬' :
                     rep.name.includes('Sarah') ? '💼' :
                     rep.name.includes('David') ? '🌍' :
                     rep.name.includes('Rachel') ? '💼' :
                     rep.name.includes('Erik') ? '📊' :
                     rep.name.includes('Lucas') ? '🚀' :
                     rep.name.includes('Jordan') ? '🎯' : '👤'}
                  </div>
                  <h3 className="font-semibold text-primary mb-2">{rep.name}'s Dashboard</h3>
                  <p className="text-sm text-text-muted mb-4">{rep.department}</p>

                  <div className="space-y-3">
                    {/* March 2026 Timeline */}
                    <div className="pt-2 border-t border-border">
                      <div className="flex justify-between items-center mb-1">
                        <span className="text-xs text-text-muted font-medium">March 2026</span>
                        <span className="text-sm font-semibold text-primary">
                          {formatCompactCurrency(rep.march2026?.total || 0)}
                        </span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-xs text-text-muted">{rep.march2026?.dealCount || 0} deals</span>
                      </div>
                    </div>

                    {/* FY 2026 Timeline (Cumulative) */}
                    <div className="pt-2 border-t border-border">
                      <div className="flex justify-between items-center mb-1">
                        <span className="text-xs text-text-muted font-medium">FY 2026 (Apr-Jun)</span>
                        <span className="text-sm font-semibold text-warning">
                          {formatCompactCurrency(rep.fy26?.total || 0)}
                        </span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-xs text-text-muted">{rep.fy26?.dealCount || 0} deals (cumulative)</span>
                      </div>
                    </div>

                    {/* FY 2027 Timeline (Cumulative - All Deals) */}
                    <div className="pt-2 border-t border-border">
                      <div className="flex justify-between items-center mb-1">
                        <span className="text-xs text-text-muted font-medium">FY 2027</span>
                        <span className="text-sm font-semibold text-success">
                          {formatCompactCurrency(rep.fy27?.total || 0)}
                        </span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-xs text-text-muted">{rep.fy27?.dealCount || 0} deals (all timelines)</span>
                      </div>
                    </div>
                  </div>
                </Card>
              </Link>
            ))}
          </div>
        </section>

        {/* Strategic Initiatives */}
        <section className="bg-surface border-2 border-border rounded-lg p-8 mb-8">
          <h2 className="text-2xl font-semibold text-primary mb-6 pb-4 border-b-2 border-primary">🎯 Strategic Initiatives</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Link to="/acquisitions">
              <Card className="hover:border-primary transition-colors cursor-pointer h-full">
                <div className="text-4xl mb-4">🏢</div>
                <h3 className="font-semibold text-primary mb-2">Strategic Acquisitions</h3>
                <p className="text-sm text-text-muted">M&A opportunities and acquisition targets</p>
              </Card>
            </Link>
            <Link to="/cost-savings">
              <Card className="hover:border-primary transition-colors cursor-pointer h-full">
                <div className="text-4xl mb-4">💰</div>
                <h3 className="font-semibold text-primary mb-2">Cost Savings</h3>
                <p className="text-sm text-text-muted">EBITDA impact initiatives and cost reduction programs</p>
              </Card>
            </Link>
            <Link to="/existing-base">
              <Card className="hover:border-primary transition-colors cursor-pointer h-full">
                <div className="text-4xl mb-4">🔄</div>
                <h3 className="font-semibold text-primary mb-2">Existing Base</h3>
                <p className="text-sm text-text-muted">Recurring revenue streams and customer retention</p>
              </Card>
            </Link>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="mt-12 border-t-2 border-border bg-surface">
        <div className="max-w-full mx-auto px-12 py-6 text-center">
          <p className="text-text-muted">
            Unique X - FY26 Sales Dashboard
          </p>
          <p className="text-sm text-text-muted mt-2">
            All dashboards feature weekly tracking, confidence indicators, and real-time progress monitoring
          </p>
        </div>
      </footer>
    </div>
  );
};
